import { useEffect, useMemo, useState } from 'react';
import { Modal, Switch, Checkbox, Space, Typography, Spin, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { firmsApi } from '../../../api/firms';
import { dataSourcesApi } from '../../../api/dataSources';

interface Props {
  firmId: number;
  roleId: number;
  open: boolean;
  onClose: () => void;
}

export function RolePermissionsModal({ firmId, roleId, open, onClose }: Props) {
  const queryClient = useQueryClient();
  const [allReports, setAllReports] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);

  const reportsQuery = useQuery({
    queryKey: ['firm-data-sources', firmId],
    queryFn: () => dataSourcesApi.getByFirm(firmId),
    enabled: open && !!firmId,
  });

  const permsQuery = useQuery({
    queryKey: ['role-permissions', firmId, roleId],
    queryFn: () => firmsApi.getRolePermissions(firmId, roleId),
    enabled: open && !!firmId && !!roleId,
  });

  useEffect(() => {
    if (permsQuery.data) {
      setAllReports(permsQuery.data.allReports);
      setSelected(permsQuery.data.sourceIds);
    }
  }, [permsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      firmsApi.updateRolePermissions(firmId, roleId, {
        allReports,
        sourceIds: allReports ? [] : selected,
      }),
    onSuccess: () => {
      message.success('İzinler kaydedildi.');
      queryClient.invalidateQueries({ queryKey: ['role-permissions', firmId, roleId] });
      onClose();
    },
    onError: () => message.error('İzinler kaydedilemedi.'),
  });

  const reports = useMemo(() => reportsQuery.data ?? [], [reportsQuery.data]);
  const loading = reportsQuery.isLoading || permsQuery.isLoading;

  const toggle = (id: number) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Modal
      title="Rapor İzinleri"
      open={open}
      onCancel={onClose}
      onOk={() => saveMutation.mutate()}
      okText="Kaydet"
      cancelText="İptal"
      confirmLoading={saveMutation.isPending}
    >
      {loading ? (
        <Spin />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Switch checked={allReports} onChange={setAllReports} />
            <Typography.Text strong>Tüm raporlar (*)</Typography.Text>
          </Space>
          {!allReports &&
            reports.map((r) => (
              <Checkbox key={r.id} checked={selected.includes(r.id)} onChange={() => toggle(r.id)}>
                {r.name}
              </Checkbox>
            ))}
        </Space>
      )}
    </Modal>
  );
}
